"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { faqData } from "@/features/landing/data";

export default function LandingFAQ() {
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  const toggleCategory = (categoryId: number) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
    setOpenQuestion(null);
  };

  const toggleQuestion = (questionId: string) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#6b6b6b] font-winky mb-2">
            FAQ
          </p>
          <h2 className="text-[1.75rem] sm:text-[2.5rem] font-bold text-[#2d2d2d] font-winky leading-tight">
            Got questions?
          </h2>
          <p className="text-[#6b6b6b] text-[14px] sm:text-[16px] font-patrick mt-3">
            Everything you need to know about Hoku.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqData.map((category) => {
            const isOpen = openCategory === category.id;
            return (
              <div
                key={category.id}
                className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                  isOpen
                    ? "bg-[#fafafa] border border-[#e0e0e0] shadow-sm"
                    : "bg-white border border-[#eee]"
                }`}
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={isOpen}
                  className="w-full px-6 py-5 flex items-center justify-between transition-colors"
                >
                  <h3 className="text-[15px] sm:text-[16px] font-bold text-[#2d2d2d] font-winky text-left">
                    {category.category}
                  </h3>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    isOpen ? "bg-[#2d2d2d]" : "bg-[#f0f0f0]"
                  }`}>
                    {isOpen ? (
                      <Minus className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <Plus className="w-3.5 h-3.5 text-[#6b6b6b]" />
                    )}
                  </div>
                </button>

                {/* Questions */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 space-y-2">
                        {category.questions.map((item, index) => {
                          const qId = `${category.id}-${index}`;
                          const qOpen = openQuestion === qId;
                          return (
                            <div key={qId}>
                              <button
                                onClick={() => toggleQuestion(qId)}
                                aria-expanded={qOpen}
                                className="w-full py-3 flex items-start justify-between text-left group"
                              >
                                <p className={`text-[13px] sm:text-[14px] pr-4 font-winky transition-colors ${
                                  qOpen ? "text-[#2d2d2d] font-bold" : "text-[#555] font-medium"
                                }`}>
                                  {item.q}
                                </p>
                                <div className={`mt-0.5 transition-transform duration-200 ${qOpen ? "rotate-45" : ""}`}>
                                  <Plus className="w-4 h-4 text-[#767676] flex-shrink-0" />
                                </div>
                              </button>

                              <AnimatePresence>
                                {qOpen && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                  >
                                    <p className="text-[#6b6b6b] text-[13px] sm:text-[14px] leading-relaxed font-patrick pb-3 pl-0.5">
                                      {item.a}
                                    </p>
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              {index < category.questions.length - 1 && (
                                <div className="border-b border-[#eee]" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
